import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarContext } from './CalendarContext';
import { TranslationProvider } from './TranslationContext';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
// Types
import { CalendarContextType } from './CalendarContext';
import { CalendarDialogMode } from './types/CalendarDialogMode';
import { Mode } from './types/Mode';
import { ICalendar } from '@trii/types/dist/Calendar/Calendar';
import { IEvent } from '@trii/types/dist/Calendar';
import {
  type EventData,
  type TaskData,
  type AddActivityRequestData,
  type UpdatedActivityRequestData,
  type AddTaskRequestData,
} from '../types';
// Hooks
import useEvent from '../hooks/useEvent';
import { useDialog } from '../hooks/useDialog';
import { useCalendar } from '../hooks/useCalendar';
import { useCalendarEvents, type CalendarEvent } from '../hooks/useCalendarEvents';
import useCalendarTasks from '../hooks/useCalendarTasks';
import useTask from '../hooks/useTask';
// Services (replaces Redux)
import * as calendarApi from '../services/calendarApi';
import { fetchContact } from '../services/api';
// Utils
import { roundToNext30Minutes } from '../utils/roundTime';
import { extractBaseEventId, getEventIdForApi } from '../utils/eventIdUtils';
// Context
import { useEventsDialogApi } from './EventsDialogApiContext';

interface Props {
  children: React.ReactNode;
  /** Props opcionales para controlar el diálogo desde fuera */
  externalOpen?: boolean;
  externalOnClose?: () => void;
  externalMode?: Mode;
  externalType?: 'event' | 'task';
  /** ID del calendario para eventos (usado como valor inicial) */
  externalEventCalendarId?: string;
  /** ID del calendario para tareas (usado como valor inicial) */
  externalTaskCalendarId?: string;
  /** Evento seleccionado para modo edit/view */
  externalSelectedEvent?: any;
  /** Tarea seleccionada para modo edit/view */
  externalSelectedTask?: any;
  /** Tabs a ocultar*/
  hiddenTabs?: boolean;
  /** ID del contacto a vincular automáticamente al crear un evento o tarea */
  externalContactId?: string;
  /** Callback invocado tras cada operación CRUD exitosa */
  externalOnActionComplete?: (
    action: 'created' | 'updated' | 'deleted',
    type: 'event' | 'task',
    data: any,
  ) => void;
  /** Fecha inicial para el evento/tarea (útil al crear desde slot de calendario) */
  externalInitialStartDate?: Date;
  /** Fecha final para el evento/tarea (útil al crear desde slot de calendario) */
  externalInitialEndDate?: Date;
}

const CalendarContextProvider = ({
  children,
  externalOpen,
  externalOnClose,
  externalMode = 'create',
  externalType = 'event',
  externalEventCalendarId,
  externalTaskCalendarId,
  externalSelectedEvent,
  externalSelectedTask,
  hiddenTabs = false,
  externalContactId,
  externalOnActionComplete,
  externalInitialStartDate,
  externalInitialEndDate,
}: Props) => {
  const { t: i18nT } = useTranslation();
  const api = useEventsDialogApi();

  // Crear wrapper para convertir el t de i18next al tipo TranslationFn esperado
  const t: (key: string, ...args: unknown[]) => string = (key: string, ...args: unknown[]) => {
    // @ts-ignore - i18nT tiene tipos muy específicos, pero funcionalmente es compatible
    return i18nT(key, ...args) as string;
  };

  const [dialogType, setDialogType] = useState<'event' | 'task'>(externalType);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [dialogMode, setDialogMode] = useState<Mode>(externalMode);
  const [taskCalendarId, setTaskCalendarId] = useState<string>(externalTaskCalendarId || '');
  const [eventCalendarId, setEventCalendarId] = useState<string>(externalEventCalendarId || '');
  const [userManuallyChangedColor, setUserManuallyChangedColor] = useState<boolean>(false);

  const [calendarDialogMode, setCalendarDialogMode] = useState<CalendarDialogMode>('create');
  const [editingCalendar, setEditingCalendar] = useState<ICalendar | null>(null);

  // Estado para el Snackbar de notificaciones de guardado
  const [savingSnackbar, setSavingSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'error';
  }>({ open: false, message: '', severity: 'info' });

  // Estado para el modal de confirmación de eliminación normal (eventos/tareas no recurrentes)
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [deleteItemType, setDeleteItemType] = useState<'event' | 'task'>('event');

  // Estado para el modal de confirmación de eliminación de eventos recurrentes
  const [deleteRecurringDialogOpen, setDeleteRecurringDialogOpen] = useState(false);

  // Estado para el modal de confirmación de edición de eventos recurrentes
  const [editRecurringDialogOpen, setEditRecurringDialogOpen] = useState(false);
  const [pendingEditFunction, setPendingEditFunction] = useState<
    ((editAll: boolean) => Promise<void>) | null
  >(null);

  // Estados de loading para operaciones
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [isUpdatingEvent, setIsUpdatingEvent] = useState(false);
  // const [isDeletingEvent, setIsDeletingEvent] = useState(false); // No usado actualmente
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [isUpdatingTask, setIsUpdatingTask] = useState(false);
  // const [isDeletingTask, setIsDeletingTask] = useState(false); // No usado actualmente

  // Estados para controlar la vista y fecha del calendario
  const CALENDAR_VIEW_STORAGE_KEY = 'trii_calendar_last_view';
  type CalendarView = 'month' | 'week' | 'day';
  const isCalendarView = (v: any): v is CalendarView =>
    v === 'month' || v === 'week' || v === 'day';

  const [calendarView, setCalendarView] = useState<CalendarView>(() => {
    try {
      const saved = localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
      return isCalendarView(saved) ? saved : 'month';
    } catch {
      return 'month';
    }
  });

  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  // Función para navegar a una fecha específica en vista día
  const navigateToDate = (date: Date) => {
    setCalendarDate(date);
    setCalendarView('day');
  };

  const eventDialog = useDialog();
  const createCalendarDialog = useDialog();

  const event = useEvent(selectedEvent ?? undefined);
  const task = useTask(selectedTask ?? undefined);

  // Sincronizar modo y tipo externos con el estado interno
  useEffect(() => {
    if (externalMode !== undefined) {
      setDialogMode(externalMode);
    }
  }, [externalMode]);

  useEffect(() => {
    if (externalType !== undefined) {
      setDialogType(externalType);
    }
  }, [externalType]);

  // Sincronizar IDs de calendarios externos con el estado interno
  useEffect(() => {
    if (externalEventCalendarId !== undefined) {
      setEventCalendarId(externalEventCalendarId);
    }
  }, [externalEventCalendarId]);

  useEffect(() => {
    if (externalTaskCalendarId !== undefined) {
      setTaskCalendarId(externalTaskCalendarId);
    }
  }, [externalTaskCalendarId]);

  // Sincronizar evento/tarea seleccionados con el estado interno
  useEffect(() => {
    if (externalSelectedEvent !== undefined) {
      setSelectedEvent(externalSelectedEvent);
      // También sincronizar el calendarId si existe
      if (externalSelectedEvent?.calendarId) {
        setEventCalendarId(externalSelectedEvent.calendarId);
      }
    }
  }, [externalSelectedEvent]);

  useEffect(() => {
    if (externalSelectedTask !== undefined) {
      console.log('[CalendarContextProvider] Sincronizando externalSelectedTask:', externalSelectedTask);
      setSelectedTask(externalSelectedTask);
      // También sincronizar el calendarId si existe
      if (externalSelectedTask?.calendarId) {
        setTaskCalendarId(externalSelectedTask.calendarId);
      }
    }
  }, [externalSelectedTask]);

  // Cuando se proporciona un contactId externo y el diálogo está abierto en modo create,
  // buscar el contacto y pre-poblar el campo LinkContactsSelect
  useEffect(() => {
    if (!externalContactId || !api) return;
    // Solo pre-poblar en modo creación
    if (externalMode !== 'create') return;
    // Solo cuando el diálogo esté abierto (o acaba de abrirse)
    if (!eventDialog.open) return;

    let cancelled = false;
    fetchContact({ baseUrl: api.baseUrl, spaceId: api.spaceId, contactId: externalContactId })
      .then((contact) => {
        if (cancelled || !contact) return;
        event.linkContact.action.resetField();
        event.linkContact.action.handleSelectContact(contact as any);
        task.linkContact.action.resetField();
        task.linkContact.action.handleSelectContact(contact as any);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalContactId, eventDialog.open]);

  // Sincronizar apertura del diálogo cuando cambia externalOpen
  useEffect(() => {
    if (externalOpen !== undefined) {
      if (externalOpen && !eventDialog.open) {
        // En modo creación externa, inicializar el formulario igual que openCreateDialog
        if (externalMode === 'create') {
          event.resetFields();
          task.resetFields();
          setUserManuallyChangedColor(false);
          setSelectedEvent(null);
          setSelectedTask(null);
          event.date.action.setError(false);
          task.date.action.setError(false);

          // Usar las fechas iniciales si están disponibles, sino usar la fecha actual
          const initialDate = externalInitialStartDate ?? new Date();
          const roundedStart = roundToNext30Minutes(initialDate);
          const roundedEnd = externalInitialEndDate 
            ? roundToNext30Minutes(externalInitialEndDate) 
            : roundedStart.add(30, 'minute');

          if (externalType === 'task') {
            task.date.action.setSelectedStartDate(roundedStart);
            task.date.action.setSelectedEndDate(roundedStart);
          } else {
            event.date.action.setSelectedStartDate(roundedStart);
            event.date.action.setSelectedEndDate(roundedEnd);
          }

          // Si no se provee un calendarId externo, intentar asignar uno disponible
          if (!externalEventCalendarId) {
            const fallback =
              activeCalendarIds?.[0] ?? myCalendars?.[0]?.id ?? calendars?.[0]?.id ?? '';
            if (fallback) setEventCalendarId(fallback);
          }
          if (!externalTaskCalendarId) {
            const fallback = activeCalendarIds?.[0] ?? calendars?.[0]?.id ?? '';
            if (fallback) setTaskCalendarId(fallback);
          }
        }
        eventDialog.handleOpen();
      } else if (!externalOpen && eventDialog.open) {
        eventDialog.handleClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  // Función personalizada para cerrar el modal de evento/tarea
  // Resetea los campos si se cierra sin guardar en modo edición
  const handleCloseEventDialog = () => {
    // Siempre resetear campos al cerrar para que los refs internos se limpien
    // y la próxima apertura sincronice correctamente
    event.resetFields();
    task.resetFields();
    // Resetear el error de fecha
    event.date.action.setError(false);
    task.date.action.setError(false);
    // Cerrar el modal
    if (externalOnClose) {
      externalOnClose();
    } else {
      eventDialog.handleClose();
    }
  };
  const calendar = useCalendar();
  const isCreateCalendar = calendarDialogMode === 'create';

  // ─── Local state replacing Redux store ────────────────
  const [calendars, setCalendars] = useState<ICalendar[]>([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState<boolean>(false);
  const [activities, setActivities] = useState<IEvent[]>([]);
  const [uid, setUid] = useState<string>('');

  const CALENDAR_SELECTED_ID_KEY = 'calendar_selected_ids';

  const [activeCalendarIds, setActiveCalendarIds] = useState<string[]>(() => {
    try {
      const storedIds = localStorage.getItem(CALENDAR_SELECTED_ID_KEY);
      return storedIds ? JSON.parse(storedIds) : [];
    } catch {
      return [];
    }
  });

  const events = useCalendarEvents(activeCalendarIds, activities);
  const tasks = useCalendarTasks(activeCalendarIds);

  const handleCloseSnackbar = () => {
    setSavingSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Helper para notificar acciones completadas
  const notifyActionComplete = useCallback(
    (action: 'created' | 'updated' | 'deleted', type: 'event' | 'task', data: any) => {
      externalOnActionComplete?.(action, type, data);
    },
    [externalOnActionComplete],
  );

  // función para normalizar las fechas de eventos y tareas
  function normalizeDates(item: any) {
    const startAt = item?.startAt ?? item?.start;
    const endAt = item?.endAt ?? item?.end ?? startAt;

    return { startAt, endAt };
  }

  // separar calendarios míos de los de otros
  const { myCalendars, otherCalendars } = useMemo(() => {
    const all = calendars ?? [];
    if (!uid) return { myCalendars: all, otherCalendars: [] as ICalendar[] };

    const mine = all.filter((c) => c.userId === uid);
    const others = all.filter((c) => c.userId !== uid);

    // Fallback: si el uid no coincide con ningún calendario, mostrar todos como propios
    if (mine.length === 0 && all.length > 0) {
      return { myCalendars: all, otherCalendars: [] as ICalendar[] };
    }

    return { myCalendars: mine, otherCalendars: others };
  }, [calendars, uid]);

  // ─── Data fetching (replaces Redux thunks) ────────────

  const refetchActivities = useCallback(async () => {
    if (!api) return;
    try {
      const now = new Date();
      const year = now.getUTCFullYear();
      const month = now.getUTCMonth();
      const start = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0)).toISOString();
      const end = new Date(Date.UTC(year, month + 2, 1, 0, 0, 0)).toISOString();
      const data = await calendarApi.fetchActivities(api, start, end);
      setActivities(data);
    } catch (e) {
      console.error('Error fetching activities:', e);
    }
  }, [api]);

  const refetchTasks = useCallback(
    async (calendarId: string) => {
      if (!api) return;
      try {
        const data = await calendarApi.fetchTasks(api, calendarId);
        tasks.setTasks(
          data.map((t: any) => ({
            id: t.id,
            title: t.title || '',
            start: new Date(t.startAt),
            end: new Date(t.endAt || t.startAt),
            startAt: t.startAt,
            endAt: t.endAt,
            description: t.description,
            calendarId: t.calendarId,
            isCompleted: t.isCompleted || false,
            relatedContact: t.relatedContact,
            kind: 'task' as const,
          })),
        );
      } catch (e) {
        console.error('Error fetching tasks:', e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api],
  );

  const refetchCalendars = useCallback(async () => {
    if (!api) return;
    setIsLoadingCalendars(true);
    try {
      const data = await calendarApi.fetchCalendars(api);
      setCalendars(data);
    } catch (e) {
      console.error('Error fetching calendars:', e);
    } finally {
      setIsLoadingCalendars(false);
    }
  }, [api]);

  // Cuando carguen los calendarios y el modal esté abierto en modo create sin calendarId,
  // asignar automáticamente el primero disponible
  useEffect(() => {
    const fallback = myCalendars?.[0]?.id ?? calendars?.[0]?.id ?? '';
    if (!fallback) return;

    // Solo asignar en modo create cuando el modal se ABRE por primera vez
    if (!eventDialog.open) return;
    
    if (dialogMode === 'create') {
      // Solo setear si está vacío
      if (!eventCalendarId) setEventCalendarId(fallback);
      if (!taskCalendarId) setTaskCalendarId(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDialog.open, dialogMode]);

  // Guard para ejecutar la carga de datos una sola vez por apertura de modal.
  // Se resetea al cerrar el modal para que la próxima apertura siempre use el
  // token vigente en ese momento y no el que había en la apertura anterior.
  const initialFetchDone = useRef(false);

  // Resetear el guard cuando el modal se cierra, para que en la próxima apertura
  // se vuelva a hacer el fetch con el token fresco (evita 401 post-refresh).
  useEffect(() => {
    if (!externalOpen) {
      initialFetchDone.current = false;
    }
  }, [externalOpen]);

  // Initial data load — se ejecuta cada vez que el modal se abre.
  // Todos los modos: calendarios (necesarios para el selector).
  // Modo edit/view: también se obtiene el uid del usuario para separar
  // "mis calendarios" de "calendarios compartidos" en la UI.
  // fetchActivities nunca se llama al abrir — solo se refresca tras mutaciones.
  useEffect(() => {
    if (!api) return;
    if (!externalOpen) return;            // esperar a que el modal esté abierto
    if (initialFetchDone.current) return; // no repetir en re-renders dentro de la misma apertura
    initialFetchDone.current = true;

    refetchCalendars();

    if (externalMode === 'create') return;

    // Fetch current user uid to separate own calendars from others'
    let ignored = false;
    calendarApi
      .fetchUserTrii(api)
      .then((user) => {
        if (!ignored) setUid(user.uid);
      })
      .catch(() => {});
    return () => {
      ignored = true;
    };
  }, [api, externalOpen, externalMode, refetchCalendars]);

  // dialog handler
  function openCreateDialog(type: 'event' | 'task', start?: Date, end?: Date) {
    setDialogType(type);
    setDialogMode('create');

    // limpiar ambos seleccionados
    setSelectedEvent(null);
    setSelectedTask(null);

    // limpiar ambos forms
    event.resetFields();
    task.resetFields();

    // Resetear flag de color cambiado manualmente
    setUserManuallyChangedColor(false);

    // Redondear las fechas al siguiente intervalo de 30 minutos
    const roundedStart = roundToNext30Minutes(start ?? new Date());
    const roundedEnd = end ? roundToNext30Minutes(end) : roundedStart.add(30, 'minute');

    // Asegurar que end sea después de start
    const finalEndDate = roundedEnd.isAfter(roundedStart)
      ? roundedEnd
      : roundedStart.add(30, 'minute');

    if (type === 'task') {
      task.date.action.setSelectedStartDate(roundedStart);
      task.date.action.setSelectedEndDate(roundedStart);
      task.date.action.setError(false);

      const fallback = activeCalendarIds?.[0] ?? calendars?.[0]?.id ?? '';

      setTaskCalendarId(fallback);
    } else {
      event.date.action.setSelectedStartDate(roundedStart);
      event.date.action.setSelectedEndDate(finalEndDate);
      event.date.action.setError(false);

      const fallback = activeCalendarIds?.[0] ?? myCalendars?.[0]?.id ?? calendars?.[0]?.id ?? '';

      setEventCalendarId(fallback);

      // Establecer el color del calendario seleccionado
      const selectedCalendar =
        myCalendars?.find((c) => c.id === fallback) ?? calendars?.find((c) => c.id === fallback);
      if (selectedCalendar?.color) {
        event.colorField.actions.changeValue(selectedCalendar.color);
      }
    }

    eventDialog.handleOpen();
  }

  // event handlers
  function handleOpenEventCreate() {
    openCreateDialog('event');
  }

  function handleStartEditingEvent() {
    // Resetear el error de fecha al cambiar a modo edición
    event.date.action.setError(false);
    task.date.action.setError(false);
    setDialogMode('edit');
  }

  function handleSelectEventItem(item: any) {
    const { startAt, endAt } = normalizeDates(item);

    const normalized = {
      ...item,
      kind: 'event',
      startAt,
      endAt,
      details: item?.details ?? '',
    };

    setDialogType('event');
    setSelectedEvent(normalized);
    setSelectedTask(null);
    
    // Si el evento tiene calendarId, usarlo; si no, usar el primer calendario disponible como fallback
    const calendarIdToUse = normalized.calendarId || myCalendars?.[0]?.id || calendars?.[0]?.id || '';
    setEventCalendarId(calendarIdToUse);
    
    setDialogMode('view');
    // Resetear el error de fecha al abrir
    event.date.action.setError(false);
    task.date.action.setError(false);
    eventDialog.handleOpen();
  }

  async function handleCreateEvent(eventData?: EventData) {
    if (!api) return;
    // Si se proporciona eventData, construir payload directamente desde ahí
    // Esto evita problemas de estado asíncrono al actualizar el formulario
    let payloadBase: Omit<AddActivityRequestData, 'calendarId'>;

    if (eventData) {
      // Construir desde eventData directamente
      payloadBase = {
        title: eventData.title,
        details: eventData.description || eventData.details || '',
        startAt: new Date(eventData.startAt),
        endAt: new Date(eventData.endAt),
        allDay: eventData.allDay,
        location: eventData.location || '',
        isPrivate: eventData.isPrivate || false,
        color: eventData.color || '',
        rule: eventData.rule || '',
        contactId: eventData.relatedContactId || externalContactId || null,
        notifications: (eventData.notifications || []).map((n: any) => ({
          id: uuidv4(),
          timeValue: n.value,
          timePeriod: n.type,
          sendToContact: n.sendToContact || false,
        })),
        eventTypeId: '',
        repeatId: '',
      };
    } else {
      // Construir desde el estado del formulario (modo legacy)
      const eventBase = event.create();

      if (!eventBase.title) {
        setSavingSnackbar({
          open: true,
          message: t('global.nameRequired'),
          severity: 'error',
        });
        return;
      }

      payloadBase = {
        ...eventBase,
        startAt: new Date(eventBase.startAt),
        endAt: new Date(eventBase.endAt),
        contactId: eventBase.contactId || (externalContactId ? externalContactId : null),
        eventTypeId: '',
        repeatId: '',
        notifications: eventBase.notifications.map((n: any) => ({
          ...n,
          id: n.id || uuidv4(),
        })),
      };
    }

    const calendarId = eventCalendarId;
    if (!calendarId) {
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
      return;
    }

    const payload: AddActivityRequestData = {
      ...payloadBase,
      calendarId,
    };

    setIsCreatingEvent(true);
    try {
      setSavingSnackbar({
        open: true,
        message: t('global.savingChanges'),
        severity: 'info',
      });

      const createdEvent = await calendarApi.addActivity(api!, payload);

      // Asegurar que el calendario del evento esté en activeCalendarIds
      if (!activeCalendarIds.includes(calendarId)) {
        setActiveCalendarIds((prev) => [...prev, calendarId]);
      }

      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      // Notificar a los consumidores ANTES de cerrar
      notifyActionComplete('created', 'event', createdEvent);
      
      // Cerrar el modal y resetear DESPUÉS de notificar
      event.resetFields();
      handleCloseEventDialog();

      // Refrescar actividades después de un pequeño delay para eventual consistency
      setTimeout(() => {
        refetchActivities();
      }, 500);
    } catch (e: any) {
      console.error('Error creando evento:', e);
      const errorMessage = e?.response?.data?.message || e?.message || t('global.errorSavingChanges');
      setSavingSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsCreatingEvent(false);
    }
  }

  async function handleSaveUpdatedEvent() {
    if (!selectedEvent?.id) return;

    const payloadBase = event.create();

    const calendarId = eventCalendarId || selectedEvent.calendarId || '';
    if (!calendarId) {
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
      return;
    }

    // MANEJO DE EVENTOS RECURRENTES:
    // Si es una ocurrencia recurrente, mostrar el diálogo para preguntar al usuario
    const isRecurringOccurrence = selectedEvent.originalOccurrenceDate != null;

    if (isRecurringOccurrence) {
      // Guardar la función de guardado para ejecutarla después de la confirmación
      setPendingEditFunction(() => async (editAll: boolean) => {
        await performEventUpdate(payloadBase, calendarId, editAll);
      });

      // Abrir el diálogo de confirmación
      setEditRecurringDialogOpen(true);
    } else {
      // Para eventos normales, guardar directamente
      await performEventUpdate(payloadBase, calendarId, false);
    }
  }

  // Función auxiliar para realizar la actualización del evento
  async function performEventUpdate(payloadBase: any, calendarId: string, editAll: boolean) {
    const isRecurringOccurrence = selectedEvent?.originalOccurrenceDate != null;

    const updatedEvent: Record<string, unknown> = {
      title: payloadBase.title,
      details: payloadBase.details,
      startAt: payloadBase.startAt,
      endAt: payloadBase.endAt,
      allDay: payloadBase.allDay,
      isPrivate: payloadBase.isPrivate,
      location: payloadBase.location,
      color: payloadBase.color,
      calendarId,
      notifications: payloadBase.notifications,
      // contactId es el campo de escritura para eventos
      contactId: (payloadBase as any).contactId ?? null,
    };

    // Solo incluir 'rule' si estamos editando todas las ocurrencias
    if (editAll || !isRecurringOccurrence) {
      updatedEvent.rule = payloadBase.rule;
    }

    // Usar parentId si es ocurrencia recurrente, sino el ID base del evento
    const eventIdToUpdate = getEventIdForApi(selectedEvent);

    const updateEventData: UpdatedActivityRequestData = {
      updatedActivity: updatedEvent,
      activityId: eventIdToUpdate,
    };

    // Si es recurrente y NO queremos editar todos, pasar originalOccurrenceDate como parámetro separado
    if (isRecurringOccurrence && !editAll) {
      updateEventData.originalOccurrenceDate = selectedEvent.originalOccurrenceDate;
    }

    setIsUpdatingEvent(true);
    try {
      const updatedEventFromApi = await calendarApi.updateActivity(api!, updateEventData);

      // Actualizar el evento seleccionado con los datos actualizados
      if (updatedEventFromApi) {
        // Preservar relatedContact del estado anterior si la respuesta del API no lo incluye
        const updatedEvent = {
          ...updatedEventFromApi,
          relatedContact: updatedEventFromApi.relatedContact ?? selectedEvent.relatedContact,
        };
        
        setSelectedEvent(updatedEvent);
        // Actualizar el eventCalendarId con el valor del backend
        if (updatedEventFromApi.calendarId) {
          setEventCalendarId(updatedEventFromApi.calendarId);
        }
        
        // Actualizar la lista de eventos localmente para que al reabrir el modal
        // los datos estén correctos sin esperar el refetch asíncrono
        events.setEvents((prevEvents) =>
          prevEvents.map((e) =>
            e.id === selectedEvent?.id
              ? {
                  ...e,
                  ...updatedEventFromApi,
                  // Preservar relatedContact si no viene en la respuesta
                  relatedContact: updatedEventFromApi.relatedContact ?? e.relatedContact,
                  start: new Date(updatedEventFromApi.startAt),
                  end: new Date(updatedEventFromApi.endAt || updatedEventFromApi.startAt),
                  // Asegurar que originalOccurrenceDate sea string si existe
                  originalOccurrenceDate: updatedEventFromApi.originalOccurrenceDate 
                    ? (typeof updatedEventFromApi.originalOccurrenceDate === 'string' 
                        ? updatedEventFromApi.originalOccurrenceDate 
                        : updatedEventFromApi.originalOccurrenceDate.toISOString())
                    : e.originalOccurrenceDate,
                } as CalendarEvent
              : e
          )
        );
      }

      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      // Notificar a los consumidores ANTES de cerrar el modal
      notifyActionComplete('updated', 'event', updatedEventFromApi);
      
      // Cerrar el modal después de notificar
      handleCloseEventDialog();

      // Refrescar la lista de eventos después de un pequeño delay
      // para dar tiempo al backend a propagar los cambios (eventual consistency)
      setTimeout(() => {
        refetchActivities();
      }, 500);
    } catch (error: any) {
      console.error('Error actualizando evento:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('global.errorSavingChanges');
      setSavingSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsUpdatingEvent(false);
    }
  }

  async function handleConfirmEditRecurring(editAll: boolean) {
    setEditRecurringDialogOpen(false);

    if (pendingEditFunction) {
      await pendingEditFunction(editAll);
      setPendingEditFunction(null);
    }
  }

  async function handleDeleteEvent() {
    // MANEJO DE EVENTOS RECURRENTES:
    // Si es una ocurrencia recurrente, mostrar modal de confirmación de recurrencia
    // Si es un evento normal, mostrar modal de confirmación simple

    const isRecurringOccurrence = selectedEvent?.originalOccurrenceDate != null;

    if (isRecurringOccurrence) {
      // Abrir modal de confirmación para eventos recurrentes
      setDeleteRecurringDialogOpen(true);
    } else {
      // Para eventos normales, abrir modal de confirmación simple
      setDeleteItemType('event');
      setConfirmDeleteDialogOpen(true);
    }
  }

  async function handleConfirmDelete() {
    if (!api) return;
    // Cerrar el modal de confirmación inmediatamente
    setConfirmDeleteDialogOpen(false);

    // Función para ejecutar la eliminación después de confirmar en el modal
    if (deleteItemType === 'event') {
      // Eliminar evento normal
      try {
        const eventIdToDelete = getEventIdForApi(selectedEvent);

        // Cerrar el modal de evento antes de eliminar para evitar intentos duplicados
        handleCloseEventDialog();

        await calendarApi.deleteActivity(api!, eventIdToDelete);

        setSavingSnackbar({
          open: true,
          message: t('global.changesSaved'),
          severity: 'success',
        });

        notifyActionComplete('deleted', 'event', { id: eventIdToDelete });
        // Refetch después de un delay para eventual consistency
        setTimeout(() => {
          refetchActivities();
        }, 500);
      } catch (error: any) {
        console.error('Error deleting event:', error);

        // Mostrar mensaje de error más específico
        const errorMessage = error?.message?.includes('duplicate key')
          ? t('global.eventAlreadyDeleted', 'El evento ya fue eliminado')
          : t('global.errorSavingChanges');

        setSavingSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      }
    } else {
      // Eliminar tarea normal
      if (!selectedTask?.id) return;

      try {
        const taskIdToDelete = extractBaseEventId(selectedTask.id);
        const calendarId = selectedTask.calendarId;

        // Cerrar el modal antes de eliminar para evitar intentos duplicados
        handleCloseEventDialog();

        await calendarApi.deleteTaskApi(api!, taskIdToDelete);

        // limpiar estado local
        setSelectedTask(null);
        task.resetFields();

        setSavingSnackbar({
          open: true,
          message: t('global.changesSaved'),
          severity: 'success',
        });

        notifyActionComplete('deleted', 'task', { id: taskIdToDelete });
        // Refetch solo tareas (no eventos) después de un delay para eventual consistency
        if (calendarId) {
          setTimeout(() => {
            refetchTasks(calendarId);
          }, 500);
        }
      } catch (error: any) {
        console.error('Error deleting task:', error);

        const errorMessage = error?.message?.includes('duplicate key')
          ? t('global.taskAlreadyDeleted', 'La tarea ya fue eliminada')
          : t('global.errorSavingChanges');

        setSavingSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      }
    }
  }

  async function handleConfirmDeleteRecurring(deleteAll: boolean) {
    // Cerrar el modal de confirmación inmediatamente
    setDeleteRecurringDialogOpen(false);

    // Según el backend:
    // - Usar parentId como activityId
    // - Si deleteAll = false: enviar originalOccurrenceDate para eliminar solo esta ocurrencia
    // - Si deleteAll = true: NO enviar originalOccurrenceDate para eliminar todas las ocurrencias

    const eventIdToDelete = getEventIdForApi(selectedEvent);

    // Cerrar el modal de evento antes de eliminar para evitar intentos duplicados
    handleCloseEventDialog();

    try {
      if (deleteAll) {
        // Eliminar todas las ocurrencias (sin originalOccurrenceDate)
        await calendarApi.deleteActivity(api!, eventIdToDelete);
      } else {
        // Eliminar solo esta ocurrencia (con originalOccurrenceDate)
        await calendarApi.deleteActivity(
          api!,
          eventIdToDelete,
          selectedEvent.originalOccurrenceDate,
        );
      }

      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      notifyActionComplete('deleted', 'event', { id: eventIdToDelete });
      // Refetch después de un delay para eventual consistency
      setTimeout(() => {
        refetchActivities();
      }, 500);
    } catch (error: any) {
      console.error('Error deleting recurring event:', error);

      const errorMessage = error?.message?.includes('duplicate key')
        ? t('global.eventAlreadyDeleted', 'El evento ya fue eliminado')
        : t('global.errorSavingChanges');

      setSavingSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  }
  async function handleEndEvent() {
    if (!api) return;
    // Para eventos recurrentes, usar el ID base o parentId
    const eventIdToEnd = getEventIdForApi(selectedEvent);
    await calendarApi.endActivity(api, eventIdToEnd);

    handleCloseEventDialog();
  }

  async function handleResizeEvent(event: any, start: Date | string, end: Date | string) {
    // Asegurar que tenemos objetos Date para react-big-calendar
    const newStartDate = start instanceof Date ? start : new Date(start);
    const newEndDate = end instanceof Date ? end : new Date(end);

    // Convertir a ISO strings para el backend
    const newStartISO = newStartDate.toISOString();
    const newEndISO = newEndDate.toISOString();

    const isTask = event.kind === 'task';
    const isRecurringOccurrence = !isTask && event.originalOccurrenceDate != null;

    // Si es una ocurrencia recurrente, mostrar diálogo de confirmación
    if (isRecurringOccurrence) {
      // Guardar el evento seleccionado para que el diálogo pueda acceder al título
      setSelectedEvent(event);

      // Guardar la función de actualización para ejecutarla después de la confirmación
      setPendingEditFunction(() => async (editAll: boolean) => {
        await performResizeUpdate(
          event,
          newStartISO,
          newEndISO,
          newStartDate,
          newEndDate,
          isTask,
          editAll,
        );
      });

      // Abrir el diálogo de confirmación
      setEditRecurringDialogOpen(true);
    } else {
      // Para eventos normales y tareas, actualizar directamente
      await performResizeUpdate(
        event,
        newStartISO,
        newEndISO,
        newStartDate,
        newEndDate,
        isTask,
        false,
      );
    }
  }

  // Función auxiliar para realizar la actualización del evento al redimensionar/arrastrar
  async function performResizeUpdate(
    event: any,
    newStartISO: string,
    newEndISO: string,
    newStartDate: Date,
    newEndDate: Date,
    isTask: boolean,
    editAll: boolean,
  ) {
    // Optimistic update: actualizar el estado local con objetos Date
    if (isTask) {
      // Las tareas se refetchean después
    } else {
      events.setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e.id === event.id
            ? {
                ...e,
                start: newStartDate,
                end: newEndDate,
                startAt: newStartDate,
                endAt: newEndDate,
              }
            : e,
        ),
      );
    }

    // Mostrar snackbar de guardando
    setSavingSnackbar({
      open: true,
      message: t('global.savingChanges'),
      severity: 'info',
    });

    try {
      if (isTask) {
        // Actualizar tarea
        const taskIdToUpdate = extractBaseEventId(event.id);
        const updatedTaskPayload = {
          title: event.title,
          description: event.description,
          startAt: newStartISO,
          endAt: newEndISO,
          calendarId: event.calendarId,
          allDay: event.allDay || false,
          isCompleted: event.isCompleted || false,
        };

        await calendarApi.updateTaskApi(api!, taskIdToUpdate, updatedTaskPayload);

        // Refetch para asegurar sincronización con el backend
        await refetchTasks(event.calendarId);
      } else {
        // Actualizar evento
        const eventIdToUpdate = getEventIdForApi(event);
        const isRecurringOccurrence = event.originalOccurrenceDate != null;

        const updatedEvent: Partial<IEvent> = {
          title: event.title,
          details: event.details,
          startAt: new Date(newStartISO),
          endAt: new Date(newEndISO),
          allDay: event.allDay,
          isPrivate: event.isPrivate,
          location: event.location,
          color: event.color,
          calendarId: event.calendarId,
        };

        // Solo incluir 'rule' si estamos editando todas las ocurrencias
        if (editAll || !isRecurringOccurrence) {
          updatedEvent.rule = event.rule;
        }

        const updateEventData: UpdatedActivityRequestData = {
          updatedActivity: updatedEvent,
          activityId: eventIdToUpdate,
        };

        // Si es recurrente y NO queremos editar todos, pasar originalOccurrenceDate como parámetro separado
        if (isRecurringOccurrence && !editAll) {
          updateEventData.originalOccurrenceDate = event.originalOccurrenceDate;
        }

        await calendarApi.updateActivity(api!, updateEventData);
      }

      // Mostrar snackbar de éxito
      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      // Refetch después de un delay para eventual consistency
      setTimeout(() => {
        refetchActivities();
      }, 500);
    } catch (error) {
      // En caso de error, revertir al estado original (solo para eventos)
      if (!isTask) {
        events.setEvents((prevEvents) =>
          prevEvents.map((e) =>
            e.id === event.id ? { ...e, start: event.start, end: event.end } : e,
          ),
        );
      }
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
    }
  }

  // función para abrir el modal de creación arrastrando el click en el calendario
  function handleOpenCreateFromSlot(start: Date, end: Date) {
    openCreateDialog('event', start, end);
  }

  function resetCreateCalendarForm() {
    // name
    calendar.nameField.actions.resetValue();
    // color
    calendar.handleColorChange('#ffffff');
    // share users
    calendar.shareWithUsers.action.resetField();

    //sync with google calendar selector
    calendar.handleGoogleCalendarChange(null);
  }

  function handleCloseCreateCalendarDialog() {
    resetCreateCalendarForm();

    setEditingCalendar(null);
    setCalendarDialogMode('create');

    createCalendarDialog.handleClose();
  }

  // función para llenar el formulario de calendario para editar
  function fillCalendarFormFromCalendar(cal: any) {
    // name/title
    calendar.nameField.actions.changeValue(cal.title ?? '');
    calendar.nameField.actions.onSuccess();
    // color
    calendar.handleColorChange(cal.color ?? '#ffffff');
    // shared users
    calendar.shareWithUsers.action.setUserIds(cal.sharedWith ?? []);
    // google calendar
    calendar.handleGoogleCalendarChange(cal.googleCalendar ?? null);
  }

  function handleOpenCreateCalendarDialog() {
    setCalendarDialogMode('create');
    setEditingCalendar(null);

    resetCreateCalendarForm();
    createCalendarDialog.handleOpen();
  }

  function handleOpenEditCalendarDialog(cal: ICalendar) {
    setCalendarDialogMode('edit');
    setEditingCalendar(cal);

    fillCalendarFormFromCalendar(cal);
    createCalendarDialog.handleOpen();
  }

  async function handleSubmitCalendar() {
    if (!api) return;
    const title = (calendar.nameField.attributes.value ?? '').trim();

    if (!title) {
      setSavingSnackbar({
        open: true,
        message: t('global.nameRequired'),
        severity: 'error',
      });
      return;
    }

    const sharedWith = calendar.shareWithUsers.field.userIds;

    const payload = {
      title,
      color: calendar.color,
      sharedWith: sharedWith.length ? sharedWith : undefined,
      googleCalendar: calendar.googleCalendar,
    };

    try {
      if (isCreateCalendar) {
        await calendarApi.addCalendarApi(api!, payload);
      } else {
        const calendarId = editingCalendar?.id;
        if (!calendarId) return;

        await calendarApi.updateCalendarApi(api!, calendarId, payload);
      }

      handleCloseCreateCalendarDialog();

      setSavingSnackbar({
        open: true,
        message: isCreateCalendar
          ? t('global.calendarCreatedSuccess')
          : t('global.calendarUpdatedSuccess'),
        severity: 'success',
      });

      refetchCalendars();
    } catch (e) {
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
    }
  }

  // HANDLERS DE TAREAS

  // función para crear una tarea
  async function handleCreateTask(taskData?: TaskData) {
    if (!api) return;
    // Si se proporciona taskData, construir payload directamente desde ahí
    // Esto evita problemas de estado asíncrono al actualizar el formulario
    let payloadBase: Omit<AddTaskRequestData, 'calendarId'>;

    if (taskData) {
      // Construir desde taskData directamente
      payloadBase = {
        title: taskData.title,
        description: taskData.description || '',
        startAt: taskData.startAt,
        contactId: taskData.relatedContactId || null,
      };
    } else {
      // Construir desde el estado del formulario (modo legacy)
      payloadBase = task.create();
    }

    if (!payloadBase.title) {
      setSavingSnackbar({
        open: true,
        message: t('global.nameRequired'),
        severity: 'error',
      });
      return;
    }

    const calendarId = taskCalendarId;
    if (!calendarId) {
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
      return;
    }

    const payload: AddTaskRequestData = {
      ...payloadBase,
      calendarId,
      contactId: (payloadBase as any).contactId || (externalContactId ? externalContactId : null),
    };

    setIsCreatingTask(true);
    try {
      setSavingSnackbar({
        open: true,
        message: t('global.savingChanges'),
        severity: 'info',
      });

      const createdTask = await calendarApi.addTask(api!, payload);

      // Asegurar que el calendario de la tarea esté en activeCalendarIds
      if (!activeCalendarIds.includes(calendarId)) {
        setActiveCalendarIds((prev) => [...prev, calendarId]);
      }

      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      // Notificar a los consumidores ANTES de cerrar
      notifyActionComplete('created', 'task', createdTask);
      
      // Cerrar el modal y resetear DESPUÉS de notificar
      task.resetFields();
      handleCloseEventDialog();

      // Refrescar solo tareas (no eventos) después de un pequeño delay para eventual consistency
      setTimeout(() => {
        refetchTasks(calendarId);
      }, 500);
    } catch (e: any) {
      console.error('Error creando tarea:', e);
      const errorMessage = e?.response?.data?.message || e?.message || t('global.errorSavingChanges');
      setSavingSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsCreatingTask(false);
    }
  }

  // función para abrir el modal de creación de tarea
  function handleOpenTaskCreate() {
    openCreateDialog('task');
  }

  // funcion para editar una tarea
  async function handleSaveUpdatedTask() {
    if (!api || !selectedTask?.id) return;

    const payloadBase = task.create();

    if (!payloadBase.title) {
      setSavingSnackbar({
        open: true,
        message: t('global.nameRequired'),
        severity: 'error',
      });
      return;
    }

    const calendarId = taskCalendarId || selectedTask.calendarId;
    if (!calendarId) {
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
      return;
    }

    const updatedTaskPayload = {
      title: payloadBase.title,
      description: payloadBase.description,
      startAt: payloadBase.startAt,
      endAt: payloadBase.endAt,
      calendarId,
      // contactId es el campo de escritura para tareas
      contactId: (payloadBase as any).contactId ?? null,
    };

    setIsUpdatingTask(true);
    try {
      const taskIdToUpdate = extractBaseEventId(selectedTask.id);
      const updatedTaskFromApi = await calendarApi.updateTaskApi(
        api!,
        taskIdToUpdate,
        updatedTaskPayload,
      );

      // refrescar selectedTask con los datos actualizados
      if (updatedTaskFromApi) {
        // Preservar relatedContact del estado anterior si la respuesta del API no lo incluye
        // Esto evita que se limpie el contacto antes de que el refetch obtenga los datos frescos
        const updatedTask = {
          ...updatedTaskFromApi,
          relatedContact: updatedTaskFromApi.relatedContact ?? selectedTask.relatedContact,
        };
        
        setSelectedTask(updatedTask);
        if (updatedTaskFromApi.calendarId) {
          setTaskCalendarId(updatedTaskFromApi.calendarId);
        }
        
        // Actualizar la lista de tareas localmente para que al reabrir el modal
        // los datos estén correctos sin esperar el refetch asíncrono
        tasks.setTasks((prevTasks: any[]) =>
          prevTasks.map((t: any) =>
            t.id === selectedTask?.id
              ? {
                  ...t,
                  ...updatedTaskFromApi,
                  // Preservar relatedContact si no viene en la respuesta
                  relatedContact: updatedTaskFromApi.relatedContact ?? t.relatedContact,
                  start: updatedTaskFromApi.startAt ? new Date(updatedTaskFromApi.startAt) : t.start,
                  end: updatedTaskFromApi.startAt ? new Date(updatedTaskFromApi.startAt) : t.end,
                }
              : t
          )
        );
      }

      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      // Notificar a los consumidores ANTES de cerrar
      notifyActionComplete('updated', 'task', updatedTaskFromApi);
      
      // Cerrar el modal después de notificar
      handleCloseEventDialog();

      // Refrescar solo tareas (no eventos) después de un pequeño delay
      // para dar tiempo al backend a propagar los cambios (eventual consistency)
      setTimeout(() => {
        const originalCalendarId = selectedTask.calendarId;
        const updatedCalendarId = updatedTaskFromApi?.calendarId || calendarId;
        refetchTasks(updatedCalendarId);
        if (originalCalendarId && originalCalendarId !== updatedCalendarId) {
          refetchTasks(originalCalendarId);
        }
      }, 500);
    } catch (e: any) {
      console.error('Error actualizando tarea:', e);
      const errorMessage = e?.response?.data?.message || e?.message || t('global.errorSavingChanges');
      setSavingSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsUpdatingTask(false);
    }
  }

  // función para eliminar una tarea
  async function handleDeleteTask() {
    if (!selectedTask?.id) return;

    // Mostrar modal de confirmación
    setDeleteItemType('task');
    setConfirmDeleteDialogOpen(true);
  }

  // función para alternar el estado de completado de una tarea
  async function handleToggleTaskCompleted() {
    if (!api || !selectedTask?.id) return;

    const newCompletedStatus = !(selectedTask.isCompleted || false);

    try {
      const taskIdToToggle = extractBaseEventId(selectedTask.id);
      const calendarId = selectedTask.calendarId;
      const updatedTaskFromApi = await calendarApi.toggleTaskCompletedApi(
        api!,
        taskIdToToggle,
        newCompletedStatus,
      );

      // Actualizar estado local con la tarea actualizada
      if (updatedTaskFromApi) {
        setSelectedTask(updatedTaskFromApi);
      }

      setSavingSnackbar({
        open: true,
        message: t('global.changesSaved'),
        severity: 'success',
      });

      // Refetch después de un delay para eventual consistency
      if (calendarId) {
        setTimeout(() => {
          refetchTasks(calendarId);
        }, 500);
      }
    } catch (e) {
      setSavingSnackbar({
        open: true,
        message: t('global.errorSavingChanges'),
        severity: 'error',
      });
    }
  }

  // función para duplicar un evento o tarea
  function handleDuplicateItem() {
    const isEvent = dialogType === 'event';
    const selectedItem = isEvent ? selectedEvent : selectedTask;

    if (!selectedItem) return;

    // Guardar referencia al item antes de limpiar el estado
    const itemCopy = { ...selectedItem };

    if (isEvent) {
      // Limpiar selección y cambiar a modo crear PRIMERO
      setSelectedEvent(null);
      setDialogMode('create');

      // Marcar que el usuario cambió el color manualmente para evitar sincronización con calendario
      setUserManuallyChangedColor(true);

      // Usar setTimeout con delay mayor para permitir que React complete el ciclo de renderizado
      // antes de llenar los campos. Esto evita que los useField se reseteen
      setTimeout(() => {
        // Duplicar los datos del evento
        event.titleField.actions.changeValue(itemCopy.title ?? '');
        event.descriptionField.actions.changeValue(itemCopy.details ?? itemCopy.description ?? '');
        event.locationField.actions.changeValue(itemCopy.location ?? '');
        event.colorField.actions.changeValue(itemCopy.color ?? '');
        event.setAllDay(itemCopy.allDay ?? false);
        event.setIsPrivate(itemCopy.isPrivate ?? false);
        event.setRecurrenceRule(itemCopy.rule ?? '');
        event.date.action.setSelectedStartDate(dayjs(itemCopy.startAt));
        event.date.action.setSelectedEndDate(dayjs(itemCopy.endAt));
        event.date.action.setError(false);

        // Resetear notificaciones (las notificaciones no se duplican típicamente)
        event.notifications.action.resetNotifications();

        // Copiar contacto relacionado si existe
        if (itemCopy.relatedContact?.id) {
          event.linkContact.action.handleSelectContact(itemCopy.relatedContact);
        }

        // Mantener el mismo calendario
        setEventCalendarId(itemCopy.calendarId ?? eventCalendarId);
      }, 50);
    } else {
      // Limpiar selección y cambiar a modo crear PRIMERO
      setSelectedTask(null);
      setDialogMode('create');

      // Usar setTimeout para permitir que React complete el ciclo de renderizado
      setTimeout(() => {
        // Duplicar los datos de la tarea
        task.titleField.actions.changeValue(itemCopy.title ?? '');
        task.descriptionField.actions.changeValue(itemCopy.description ?? '');
        task.date.action.setSelectedStartDate(dayjs(itemCopy.startAt));
        task.date.action.setSelectedEndDate(dayjs(itemCopy.startAt)); // tareas tienen mismo start y end
        task.date.action.setError(false);

        // Copiar contacto relacionado si existe
        if (itemCopy.relatedContact?.id) {
          task.linkContact.action.handleSelectContact(itemCopy.relatedContact);
        }

        // Mantener el mismo calendario
        setTaskCalendarId(itemCopy.calendarId ?? taskCalendarId);
      }, 50);
    }
  }

  // función para manejar la selección de un item de tipo tarea y abrir el modal correspondiente.
  function handleSelectTaskItem(item: any) {
    const { startAt, endAt } = normalizeDates(item);

    const normalized = {
      ...item,
      kind: 'task',
      startAt,
      endAt,
      description: item?.description ?? '',
    };

    setDialogType('task');
    setSelectedTask(normalized);
    setTaskCalendarId(normalized.calendarId ?? '');
    setEventCalendarId(normalized.calendarId ?? '');
    setSelectedEvent(null);
    setDialogMode('view');
    // Resetear el error de fecha al abrir
    event.date.action.setError(false);
    task.date.action.setError(false);
    eventDialog.handleOpen();
  }

  // función para manejar la selección de un item (evento o tarea) y abrir el modal correspondiente.
  function handleSelectEvent(item: any) {
    if (item?.kind === 'task') return handleSelectTaskItem(item);
    return handleSelectEventItem(item);
  }

  // Guardar el calendarId seleccionado en localStorage
  useEffect(() => {
    try {
      if (activeCalendarIds)
        localStorage.setItem(CALENDAR_SELECTED_ID_KEY, JSON.stringify(activeCalendarIds));
    } catch {}
  }, [activeCalendarIds]);

  useEffect(() => {
    if (!calendars?.length) return;

    setActiveCalendarIds((prev) => {
      const existing = new Set(calendars.map((c) => c.id));
      const cleaned = (prev ?? []).filter((id) => existing.has(id));

      // si habia selección válida, la mantenemos
      if (cleaned.length) return cleaned;

      // si no hay nada seleccionado, seleccionamos todo por defecto
      return calendars.map((c) => c.id);
    });
  }, [calendars]);

  // Persistir vista del calendario cuando cambie
  useEffect(() => {
    try {
      localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, calendarView);
    } catch {}
  }, [calendarView]);

  const value: CalendarContextType = {
    eventDialog: {
      ...eventDialog,
      handleClose: handleCloseEventDialog,
    },
    dialogMode,
    createCalendarDialog,
    event,
    calendar,
    events,
    handleCreateEvent,
    handleSelectEvent,
    handleOpenEventCreate,
    handleStartEditingEvent,
    handleSaveUpdatedEvent,
    handleEndEvent,
    handleDeleteEvent,
    handleResizeEvent,
    selectedEvent,
    savingSnackbar,
    handleCloseSnackbar,
    dialogType,
    setDialogType,
    handleOpenTaskCreate,
    handleOpenCreateFromSlot,
    handleCloseCreateCalendarDialog,
    calendarDialogMode,
    editingCalendar,
    handleOpenCreateCalendarDialog,
    handleOpenEditCalendarDialog,
    handleSubmitCalendar,
    tasks,
    activeCalendarIds,
    setActiveCalendarIds,
    handleCreateTask,
    task,
    selectedTask,
    taskCalendarId,
    setTaskCalendarId,
    eventCalendarId,
    setEventCalendarId,
    userManuallyChangedColor,
    setUserManuallyChangedColor,
    myCalendars,
    otherCalendars,
    isLoadingCalendars,
    isCreatingEvent,
    isUpdatingEvent,
    // isDeletingEvent, // Commented out - not used
    isCreatingTask,
    isUpdatingTask,
    // isDeletingTask, // Commented out - not used
    handleSaveUpdatedTask,
    handleDeleteTask,
    handleToggleTaskCompleted,
    handleDuplicateItem,
    calendarView,
    setCalendarView,
    calendarDate,
    setCalendarDate,
    navigateToDate,
    confirmDeleteDialog: {
      open: confirmDeleteDialogOpen,
      handleClose: () => setConfirmDeleteDialogOpen(false),
      itemType: deleteItemType,
    },
    handleConfirmDelete,
    deleteRecurringDialog: {
      open: deleteRecurringDialogOpen,
      handleClose: () => setDeleteRecurringDialogOpen(false),
    },
    handleConfirmDeleteRecurring,
    editRecurringDialog: {
      open: editRecurringDialogOpen,
      handleClose: () => {
        setEditRecurringDialogOpen(false);
        setPendingEditFunction(null);
      },
    },
    handleConfirmEditRecurring,
    hiddenTabs,
  };

  return (
    <TranslationProvider t={t}>
      <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>
    </TranslationProvider>
  );
};

export default CalendarContextProvider;
